function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}
class SectionId {
  static #separator = '__';
  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }
  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }
  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}
class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));
    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;
    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });
    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';
    postProcessCallbacks?.forEach((callback) => callback(newNode));
    setTimeout(() => oldNode.remove(), 500);
  }
  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));
  const nextEl = summary.nextElementSibling;
  if (nextEl && nextEl.getAttribute('id')) {
    summary.setAttribute('aria-controls', nextEl.id);
  }
  summary.addEventListener('click', (event) => {
    const details = event.currentTarget.closest('details');
    if (details) {
      event.currentTarget.setAttribute('aria-expanded', !details.hasAttribute('open'));
    }
  });
  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});
const trapFocusHandlers = {};
function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];
  removeTrapFocus();
  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };
  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };
  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };
  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);
  elementToFocus.focus();
  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}
// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}
function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;
  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });
  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });
  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');
      if (mouseClick) return;
      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}
function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('video.card_video').forEach((card_video) => card_video.play());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}
function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }
  quantityUpdateUnsubscriber = undefined;
  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }
  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }
  onInputChange(event) {
    this.validateQtyRules();
  }
  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const button = event.currentTarget;
    button.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}
customElements.define('quantity-input', QuantityInput);
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}
/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}
Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};
Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};
Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};
Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};
  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);
  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};
Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);
  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));
  this.initCountry();
  this.initProvince();
};
Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },
  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },
  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);
    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }
      this.provinceContainer.style.display = '';
    }
  },
  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },
  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};
class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }
  resetMobileFigmaFacetAccordions() {
    const form = this.querySelector('#FacetFiltersFormMobile.mobile-facets--figma');
    if (!form) return;
    form.querySelectorAll('details.mobile-facets__details.js-filter').forEach((d) => {
      d.setAttribute('open', '');
    });
  }
  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    // Only back / close controls — never bind every button (would steal clicks)
    this.querySelectorAll('.menu-drawer__close-button').forEach((button) => {
      button.addEventListener('pointerup', this.onCloseButtonPointerUp.bind(this));
      button.addEventListener('click', this.onCloseButtonClick.bind(this));
    });
    this.addEventListener('click', this.onDrawerLinkClick.bind(this));
  }
  onCloseButtonPointerUp(event) {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    if (event.target.closest('.slider-counter__link')) return;
    event.preventDefault();
    event.stopPropagation();
    const detailsElement = event.currentTarget.closest('details');
    if (!detailsElement || detailsElement === this.mainDetailsToggle) return;
    this.closeSubmenu(detailsElement);
  }
  onDrawerLinkClick(event) {
    const link = event.target.closest('#menu-drawer a[href]');
    if (!link || !this.contains(link)) return;
    if (link.closest('localization-form, .disclosure, country-localization, language-localization')) return;
    // Never treat category summary / back controls as navigation
    if (link.closest('summary, .menu-drawer__close-button')) return;

    const href = link.getAttribute('href');
    if (!href || href === '#' || href.startsWith('javascript:')) return;
    if (href.startsWith('#') && !href.startsWith('/#')) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    // Block ghost clicks while drill-down is opening/closing
    const busySubmenu = link.closest('details[open]:not(.is-submenu-ready)');
    if (busySubmenu && busySubmenu !== this.mainDetailsToggle) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (this._submenuAnimating) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Immediate navigate — no soft-close delay (avoids white flash / glitchy transitions)
    event.preventDefault();
    event.stopPropagation();
    if (this._isNavigating) return;
    this._isNavigating = true;
    window.location.assign(href);
  }
  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;
    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }
  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    function addTrapFocus() {
      if (summaryElement.nextElementSibling && detailsElement.querySelector('button')) {
        trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      }
      if (summaryElement.nextElementSibling) {
        summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
      }
    }
    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
      if (window.matchMedia('(max-width: 992px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      // Parent category rows (Rings / Necklaces) only open drill-down — never navigate
      event.preventDefault();
      event.stopPropagation();
      if (event.cancelable) event.stopImmediatePropagation();

      if (isOpen) {
        this.closeSubmenu(detailsElement);
        return;
      }

      // Clear leftover / stuck drill-down before opening (Rings → back → Necklaces)
      this.resetDrillDownState(detailsElement);

      this._submenuAnimating = true;
      if (parentMenuElement) parentMenuElement.classList.add('submenu-open');
      detailsElement.classList.remove('is-submenu-ready');
      detailsElement.setAttribute('open', '');
      // Force solid paint before slide-in (double rAF = layout committed)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          detailsElement.classList.add('menu-opening');
          summaryElement.setAttribute('aria-expanded', true);

          const submenu = detailsElement.querySelector(':scope > .menu-drawer__submenu');
          const markReady = (e) => {
            if (e && e.target !== submenu) return;
            if (e && e.propertyName && e.propertyName !== 'transform') return;
            detailsElement.classList.add('is-submenu-ready');
            this._submenuAnimating = false;
            if (submenu) submenu.removeEventListener('transitionend', markReady);
          };
          if (submenu) {
            submenu.addEventListener('transitionend', markReady);
            setTimeout(() => markReady({ target: submenu, propertyName: 'transform' }), 220);
          } else {
            markReady({ target: null, propertyName: 'transform' });
          }

          if (!reducedMotion || reducedMotion.matches) {
            addTrapFocus();
          } else if (submenu) {
            submenu.addEventListener('transitionend', addTrapFocus);
          }
        });
      });
    }
  }
  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    this.lockBodyScroll();
    this.resetMobileFigmaFacetAccordions();
  }
  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;
    this.resetDrillDownState();
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.querySelectorAll('.submenu-open').forEach((el) => el.classList.remove('submenu-open'));
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      details.classList.remove('is-submenu-ready');
    });
    this.unlockBodyScroll();
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }
  /** iOS-safe page scroll lock while hamburger menu is open */
  lockBodyScroll() {
    if (document.body.classList.contains('menu-drawer-scroll-lock')) return;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.dataset.menuDrawerScrollY = String(scrollY);
    document.documentElement.classList.add('menu-drawer-scroll-lock', 'overflow-hidden');
    document.body.classList.add(
      'menu-drawer-scroll-lock',
      'overflow-hidden',
      `overflow-hidden-${this.dataset.breakpoint || 'tablet'}`
    );
    document.body.style.top = `-${scrollY}px`;
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.left = '0';
    document.body.style.right = '0';
  }
  unlockBodyScroll() {
    const scrollY = parseInt(document.body.dataset.menuDrawerScrollY || '0', 10) || 0;
    document.documentElement.classList.remove('menu-drawer-scroll-lock', 'overflow-hidden');
    document.body.classList.remove(
      'menu-drawer-scroll-lock',
      'overflow-hidden',
      'overflow-hidden-mobile',
      'overflow-hidden-tablet',
      'overflow-hidden-desktop'
    );
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.left = '';
    document.body.style.right = '';
    delete document.body.dataset.menuDrawerScrollY;
    window.scrollTo(0, scrollY);
  }
  onFocusOut(event) {
    // relatedTarget still inside drawer → ignore
    const related = event.relatedTarget;
    if (related && (this.contains(related) || this.mainDetailsToggle.contains(related))) return;

    setTimeout(() => {
      if (!this.mainDetailsToggle?.hasAttribute('open')) return;
      // Opening / closing a drill-down moves focus — never close the whole menu
      if (this._submenuAnimating || this._closingSubmenu) return;

      const active = document.activeElement;
      // iOS + body scroll-lock: tap often blurs to <body> — keep menu open
      if (!active || active === document.body || active === document.documentElement) return;
      if (this.contains(active) || this.mainDetailsToggle.contains(active)) return;
      if (active.closest?.('#menu-drawer, header-drawer, .menu-drawer-container, .header__icon--menu')) return;

      // Real leave (e.g. tabbed outside) — close via X summary
      const summary = this.mainDetailsToggle.querySelector('summary');
      this.closeMenuDrawer(true, summary);
    }, 0);
  }
  onCloseButtonClick(event) {
    if (event.target.closest('.slider-counter__link')) {
      return;
    }
    // Touch/pen already handled in pointerup — skip duplicate click
    if (event.pointerType === 'touch' || event.pointerType === 'pen') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const detailsElement = event.currentTarget.closest('details');
    if (!detailsElement || detailsElement === this.mainDetailsToggle) return;
    this.closeSubmenu(detailsElement);
  }
  closeSubmenu(detailsElement) {
    if (!detailsElement || detailsElement === this.mainDetailsToggle) return;
    if (!detailsElement.hasAttribute('open')) {
      this._submenuAnimating = false;
      this._closingSubmenu = null;
      return;
    }
    // Ignore duplicate back taps while this panel is already closing
    if (this._closingSubmenu === detailsElement) return;

    this._closingSubmenu = detailsElement;
    this._submenuAnimating = true;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const submenu = detailsElement.querySelector(':scope > .menu-drawer__submenu');

    detailsElement.querySelector('summary')?.setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));

    // Instant close: no slide-out — prevents ghost header line under SALE / sticky panels on iOS
    if (parentMenuElement) parentMenuElement.classList.remove('submenu-open');
    detailsElement.removeAttribute('open');
    detailsElement.classList.remove('menu-opening', 'is-submenu-ready');
    if (submenu) {
      submenu.style.willChange = '';
      submenu.style.transform = '';
      submenu.style.opacity = '';
      submenu.style.visibility = '';
    }

    const unlock = () => {
      this._submenuAnimating = false;
      this._closingSubmenu = null;
      if (detailsElement.closest('details[open]')) {
        trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
      }
    };
    // Next frame so a rapid re-open doesn't fight mid-close layout
    requestAnimationFrame(unlock);
  }

  /** Close every drill-down except optionally one that's about to open */
  resetDrillDownState(exceptDetails = null) {
    this.querySelectorAll('.submenu-open').forEach((el) => el.classList.remove('submenu-open'));
    this.querySelectorAll('#menu-drawer details[open], .menu-drawer details[open]').forEach((details) => {
      if (details === this.mainDetailsToggle || details === exceptDetails) return;
      details.removeAttribute('open');
      details.classList.remove('menu-opening', 'is-submenu-ready');
      details.querySelector('summary')?.setAttribute('aria-expanded', false);
    });
    this._closingSubmenu = null;
    this._submenuAnimating = false;
  }
  closeAnimation(detailsElement) {
    let animationStart;
    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }
      const elapsedTime = time - animationStart;
      if (elapsedTime < 180) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };
    window.requestAnimationFrame(handleAnimation);
  }
}
document.querySelectorAll('.mobile-facets__close').forEach((closeButton) => {
  closeButton.addEventListener('click', (event) => {
    const detailsElement = closeButton.closest('details');
    if (detailsElement) {
      detailsElement.classList.remove('menu-opening');
      closeButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden-mobile');
      document.body.classList.remove('overflow-hidden');
      const drawer = detailsElement.closest('menu-drawer');
      if (drawer && drawer.closeAnimation) {
        drawer.closeAnimation(detailsElement);
      } else {
        detailsElement.removeAttribute('open');
      }
    }
  });
   closeButton.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closeButton.click();
    }
  });
});
customElements.define('menu-drawer', MenuDrawer);
class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }
  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.header-wrapper');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    this.header.classList.add('menu-open');
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    this.lockBodyScroll();
    this.resetMobileFigmaFacetAccordions();
  }
  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) {
      // Still unlock if focus target missing (focusout / edge cases)
      this.unlockBodyScroll();
      this.header?.classList.remove('menu-open');
      window.removeEventListener('resize', this.onResize);
      return;
    }
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }
  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}
customElements.define('header-drawer', HeaderDrawer);
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }
  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    // this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }
  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }
  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);
class ModalOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button');
    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }
  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}
customElements.define('deferred-media', DeferredMedia);
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButtons = Array.from(this.querySelectorAll('button[name="previous"]'));
    this.nextButtons = Array.from(this.querySelectorAll('button[name="next"]'));
    this.prevButton = this.prevButtons[0] || null;
    this.nextButton = this.nextButtons[0] || null;
    if (!this.slider || !this.nextButton) return;
    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);
    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButtons.forEach((btn) => btn.addEventListener('click', this.onButtonClick.bind(this)));
    this.nextButtons.forEach((btn) => btn.addEventListener('click', this.onButtonClick.bind(this)));
  }
  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }
  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }
  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;
    const previousPage = this.currentPage;
    const itemOffset = this.sliderItemOffset || 1;
    this.currentPage = Math.round(this.slider.scrollLeft / itemOffset) + 1;
    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }
    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }
    if (this.enableSliderLooping) return;

    const maxScroll = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    const atStart = this.slider.scrollLeft <= 2;
    const atEnd = this.slider.scrollLeft >= maxScroll - 2;
    const disablePrev =
      atStart ||
      (this.sliderItemsToShow[0] &&
        this.isSlideVisible(this.sliderItemsToShow[0]) &&
        this.slider.scrollLeft === 0);
    const lastItem = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
    const disableNext = atEnd || (lastItem && this.isSlideVisible(lastItem));

    (this.prevButtons || []).forEach((btn) => {
      if (disablePrev) btn.setAttribute('disabled', 'disabled');
      else btn.removeAttribute('disabled');
    });
    (this.nextButtons || []).forEach((btn) => {
      if (disableNext) btn.setAttribute('disabled', 'disabled');
      else btn.removeAttribute('disabled');
    });
  }
  isSlideVisible(element, offset = 0) {
    if (!element || !this.slider) return false;
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide + 2 && element.offsetLeft >= this.slider.scrollLeft - 2;
  }
  onButtonClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (!button || button.hasAttribute('disabled')) return;
    if (!this.slider || !this.sliderItemOffset) return;

    const step = Number(button.dataset.step || 1) || 1;
    const maxScroll = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    let position =
      button.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;

    position = Math.max(0, Math.min(position, maxScroll));

    // Already at start/end — do nothing (prevents empty-space / arrow "shift")
    if (Math.abs(position - this.slider.scrollLeft) < 1) {
      this.update();
      return;
    }

    this.slideScrollPosition = position;
    this.setSlidePosition(this.slideScrollPosition);
  }
  setSlidePosition(position) {
    const maxScroll = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    const clamped = Math.max(0, Math.min(position, maxScroll));
    this.slider.scrollTo({
      left: clamped,
    });
  }
}
customElements.define('slider-component', SliderComponent);
class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;
    if (!this.sliderControlWrapper) return;
    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;
    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;
    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();
    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });
      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }
    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }
  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));
    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }
  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;
    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }
    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }
    this.setSlidePosition(this.slideScrollPosition);
    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }
  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }
  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');
    if (!this.sliderControlButtons.length) return;
    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    const productSlide = this.sliderControlButtons[this.currentPage - 1];
    if (productSlide) {
      productSlide.classList.add('slider-counter__link--active');
      productSlide.setAttribute('aria-current', true);
    }
    // this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    // this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }
  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }
  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }
  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }
  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }
  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }
  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }
  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;
    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }
  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }
  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;
    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;
    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;
    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];
    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';
    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;
    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';
    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);
    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }
  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}
customElements.define('slideshow-component', SlideshowComponent);
class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.addEventListener('change', (event) => {
      // Legacy chain size/type as line-item properties — ignore so product-info does not wipe selection.
      // Native Shopify Chain Size options (name="options[...]") must still update the variant.
      const chainPicker = event.target.closest?.('[data-figma-chain-picker]');
      if (chainPicker && !chainPicker.hasAttribute('data-figma-chain-native-option')) {
        return;
      }
      if (
        event.target.matches?.('[data-figma-chain-native]') &&
        String(event.target.name || '').startsWith('properties[')
      ) {
        return;
      }
      const target = this.getInputForEventTarget(event.target);
      if (!target || (target.tagName === 'OPTION' && !target.dataset.optionValueId)) {
        return;
      }
      this.updateSelectionMetadata(event);
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }
  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;
    if (tagName === 'SELECT' && target.selectedOptions.length) {
      // Skip property-based chain selects only
      if (
        String(target.name || '').startsWith('properties[') ||
        (target.hasAttribute('data-figma-chain-native') &&
          target.closest('[data-figma-chain-picker]') &&
          !target.closest('[data-figma-chain-native-option]'))
      ) {
        return;
      }
      const previouslySelected = Array.from(target.options).find((option) => option.getAttribute('selected'));
      if (previouslySelected) previouslySelected.removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');
      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        ?.querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }
      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`fieldset`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }
  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }
  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll(
        'select:not([name^="properties["]) option[selected], fieldset input:checked'
      )
    )
      .filter((el) => {
        // Unconfirmed Figma chain Size/Type must not drive variant fetches
        const chainPicker = el.closest?.('[data-figma-chain-picker]');
        if (chainPicker && chainPicker.getAttribute('data-figma-chain-confirmed') !== 'true') {
          return false;
        }
        // Unconfirmed ring/bracelet size — keep placeholder UI from sending default US 4 etc.
        const sizePicker = el.closest?.('[data-figma-size-picker]');
        if (sizePicker && sizePicker.getAttribute('data-figma-size-confirmed') !== 'true') {
          return false;
        }
        return true;
      })
      .map(({ dataset }) => dataset.optionValueId)
      .filter(Boolean);
  }
}
customElements.define('variant-selects', VariantSelects);
class ProductRecommendations extends HTMLElement {
  observer = undefined;
  constructor() {
    super();
  }
  connectedCallback() {
    if (this.dataset.immediateLoad === 'true') {
      this.loadRecommendations(this.dataset.productId);
      return;
    }
    this.initializeRecommendations(this.dataset.productId);
  }
  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }
  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        } else if (this.classList.contains('cart-drawer-complete-look-recs')) {
          this.innerHTML = '';
          const block = this.closest('[data-cart-drawer-complete-look]');
          if (block) block.hidden = true;
        }
        if (!this.querySelector('slider-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }
        if (html.querySelector('.grid__item') || html.querySelector('.cart-drawer-complete__item')) {
          this.classList.add('product-recommendations--loaded');
        }
        if (this.classList.contains('cart-drawer-complete-look-recs')) {
          const block = this.closest('[data-cart-drawer-complete-look]');
          if (block) {
            block.hidden = !this.querySelector('.cart-drawer-complete__item');
          }
          this.dispatchEvent(new CustomEvent('cart-drawer-complete-look-updated', { bubbles: true }));
        }
      })
      .catch((e) => {
        console.error(e);
        if (this.classList.contains('cart-drawer-complete-look-recs')) {
          const block = this.closest('[data-cart-drawer-complete-look]');
          if (block) block.hidden = true;
        }
      });
  }
}
customElements.define('product-recommendations', ProductRecommendations);
// Carousel
class SliderCarousel extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._nativeMq = null;
    this._nativeMqHandler = null;
    let optionsData = {};
    let defaults = {};
    let autoplay = false;
    if(this.dataset.sectionSetting){
      const sectionSetting = JSON.parse(this.dataset.sectionSetting);
      const space = +this.dataset.spacingGridHorizontal;
      const spacemobile =  +this.dataset.spacingGridHorizontal/2;
      const figmaOtherStrip = this.hasAttribute('data-figma-other-strip');
      if (figmaOtherStrip) {
        optionsData = {
          slidesPerView: 'auto',
          spaceBetween: 5,
          loop: false,
          watchOverflow: true,
        };
        if (sectionSetting.pagination_style === 'none') {
          optionsData.pagination = false;
        } else {
          optionsData.pagination = {
            clickable: true,
            el: '.swiper-pagination',
          };
        }
        if (sectionSetting.pagination_style === 'arrows') {
          optionsData.navigation = {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          };
        }
      } else {
      optionsData = {
        slidesPerView: sectionSetting.columns_desktop,
        spaceBetween: space,
        loop: false,
        watchOverflow: true,
        centeredSlides: false,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
        resistanceRatio: 0,
        dragClickables: false,
        pagination: {
          clickable: 'true',
        },
        breakpoints: {
          0: {
            slidesPerView: 2.3,
            spaceBetween: spacemobile,
          },
          768: {
            slidesPerView: sectionSetting.columns_desktop,
            spaceBetween: space,
          }
        }
      };
      if(this.dataset.breakpoints){
        const breakpoints = JSON.parse(this.dataset.breakpoints)
        const figmaMobileGrid = this.hasAttribute('data-figma-mobile-grid');
        const figmaGap = Number(this.dataset.spacingGridHorizontal) || 10;
        /* Varsayılan 0/768 ile birleşince eski 768, özel "771" ile çakışıyordu */
        optionsData['breakpoints'] = {};
        for (const key in breakpoints) {
          const bpNum = parseInt(key, 10);
          if (key == 0 || key === '0'){
            let obj = {
              slidesPerView: breakpoints[key] === 'auto' ? 'auto' : Number(breakpoints[key]),
              /* Figma mobil 2×2: gap data-spacing-grid-horizontal ile (8px) */
              spaceBetween: figmaMobileGrid ? figmaGap : spacemobile,
            }
            /* <600px: 2×2 grid (spaceBetween = kart aralığı, px) */
            if (figmaMobileGrid && Number(breakpoints[key]) === 2) {
              obj.grid = { rows: 2, fill: 'row' };
            }
            optionsData['breakpoints'][0] = obj
          }else{
            let customspaceBetween = space
            let breakpointsKey = key
            if(key.indexOf('m') > -1){ 
              breakpointsKey = key.split('-')[1]
              customspaceBetween = spacemobile
            } else if (!Number.isNaN(bpNum)) {
              breakpointsKey = bpNum
            }
            let obj = {
              slidesPerView: breakpoints[key] === 'auto' ? 'auto' : Number(breakpoints[key]),
              spaceBetween: customspaceBetween,
            };
            /* Swiper breakpoint merge: çok satırlı grid kapatılmazsa üst breakpoint’te rows:2 kalabilir */
            if (figmaMobileGrid) {
              obj.grid = { rows: 1, fill: 'row' };
            }
            optionsData['breakpoints'][breakpointsKey] = obj
          }
        }
      }
      }
      if(sectionSetting.auto_rotate == true){
        autoplay = true;
        optionsData['autoplay'] = {
          delay: sectionSetting.change_slides_speed * 1000,
          disableOnInteraction: false,
        };
      }
      if (!figmaOtherStrip) {
        if(sectionSetting.pagination_style == 'arrows' && !this.hasAttribute('data-nav-in-header')){
          optionsData['navigation'] = {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          };
          optionsData['pagination']['el'] = '.swiper-pagination';
        }
        if(sectionSetting.pagination_style == 'dots'){
          optionsData['pagination']['el'] = '.swiper-pagination';
        }
      }
    }
    this.options = {
      ...defaults,
      ...optionsData
    };
  }

  /** collections-tabs: data-native-scroll-max="599" → ≤599px Swiper yok; CSS grid + overflow-x (inline still çakışması yok) */
  getNativeScrollMediaQuery() {
    const raw = this.dataset.nativeScrollMax;
    if (raw === undefined || raw === '') return null;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 1) return null;
    return window.matchMedia(`(max-width: ${n}px)`);
  }

  _syncNativeScroll(el) {
    if (!this._nativeMq) return;
    if (this._nativeMq.matches) {
      if (this._swiper) {
        try {
          this._swiper.destroy(true, true);
        } catch (e) {
          /* ignore */
        }
        this._swiper = null;
      }
      this.classList.add('slider-carousel--native-scroll');
      return;
    }
    this.classList.remove('slider-carousel--native-scroll');
    this._mountSwiperAndHooks(el);
  }

  _mountSwiperAndHooks(el) {
    if (this._swiper) return;
    this._swiper = new Swiper(el, this.options);
    const swiper = this._swiper;

    requestAnimationFrame(() => {
      try {
        swiper.update();
      } catch (e) {
        /* ignore */
      }
    });

    /* Eski inline 2×2: yalnızca data-native-scroll-max YOKSA (aksi halde mobilde CSS kullanılır) */
    if (this.hasAttribute('data-figma-mobile-grid') && !this.dataset.nativeScrollMax) {
      const gapPx = Number(this.dataset.spacingGridHorizontal) || 8;
      const mq = window.matchMedia('(max-width: 599px)');
      const lockMobile2x2Grid = () => {
        const wrap = el.querySelector('.swiper-wrapper');
        if (!wrap) return;
        if (mq.matches) {
          wrap.style.setProperty('display', 'grid', 'important');
          wrap.style.setProperty('grid-template-columns', 'repeat(2, minmax(0, 1fr))', 'important');
          wrap.style.setProperty('column-gap', `${gapPx}px`, 'important');
          wrap.style.setProperty('row-gap', '15px', 'important');
          wrap.style.setProperty('width', '100%', 'important');
          wrap.style.setProperty('max-width', '100%', 'important');
          wrap.style.setProperty('min-width', '0', 'important');
          wrap.style.setProperty('transform', 'none', 'important');
          wrap.querySelectorAll('.swiper-slide').forEach((slide) => {
            slide.style.setProperty('width', '100%', 'important');
            slide.style.setProperty('max-width', '100%', 'important');
            slide.style.setProperty('min-width', '0', 'important');
            slide.style.setProperty('margin', '0', 'important');
          });
        } else {
          [
            'display',
            'grid-template-columns',
            'column-gap',
            'row-gap',
            'width',
            'max-width',
            'min-width',
            'transform',
          ].forEach((p) => wrap.style.removeProperty(p));
          wrap.querySelectorAll('.swiper-slide').forEach((slide) => {
            ['width', 'max-width', 'min-width', 'margin'].forEach((p) =>
              slide.style.removeProperty(p)
            );
          });
        }
      };
      const run = () => {
        requestAnimationFrame(() => {
          swiper.update();
          lockMobile2x2Grid();
        });
      };
      swiper.on('update', () => {
        if (mq.matches) lockMobile2x2Grid();
      });
      swiper.on('breakpoint', lockMobile2x2Grid);
      mq.addEventListener('change', run);
      run();
    } else if (this.hasAttribute('data-figma-other-strip')) {
      requestAnimationFrame(() => {
        swiper.update();
      });
    }
  }

  connectedCallback() {
    const el = this.querySelector('.swiper');
    if (!el) return;

    this._nativeMq = this.getNativeScrollMediaQuery();
    if (this._nativeMq) {
      this._nativeMqHandler = () => this._syncNativeScroll(el);
      this._nativeMq.addEventListener('change', this._nativeMqHandler);
      this._syncNativeScroll(el);
      return;
    }

    this._mountSwiperAndHooks(el);
  }

  disconnectedCallback() {
    if (this._nativeMq && this._nativeMqHandler) {
      this._nativeMq.removeEventListener('change', this._nativeMqHandler);
      this._nativeMq = null;
      this._nativeMqHandler = null;
    }
    if (this._swiper) {
      try {
        this._swiper.destroy(true, true);
      } catch (e) {
        /* ignore */
      }
      this._swiper = null;
    }
    this.classList.remove('slider-carousel--native-scroll');
  }
}
customElements.define('slider-carousel', SliderCarousel);
// Variant hover
class ColorSwatch extends VariantSelects {
  constructor() { 
    super();
    const fieldsets = Array.from(this.querySelectorAll('fieldset label'));
    this.querySelectorAll('fieldset label').forEach((ele)=>{
      ele.addEventListener('mouseenter', this.onVariantChangeHover.bind(this));
      ele.addEventListener('click', this.onLabelClick.bind(this));
    }) 
  }
  onLabelClick(event){
    // Collection / homepage cards: move the black ring by checking the radio exclusively
    if (this.dataset.layout === 'card') {
      const label = event.target.closest('label');
      if (!label || !this.contains(label)) return;
      const forId = label.getAttribute('for');
      const input = forId ? document.getElementById(forId) : null;
      if (!input || !this.contains(input)) return;
      event.preventDefault();
      this.querySelectorAll('fieldset input[type="radio"]').forEach((radio) => {
        radio.checked = radio === input;
      });
      input.checked = true;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const label = event.target.closest('label');
    const href = label?.dataset?.href;
    if (href) {
      window.location.href = href;
    }
  }
  updatelabelDataset(ele){
    let href = ele.dataset.href;
    let optionValuesStr = '';
    if (this.selectedOptionValues.length) {
          optionValuesStr=this.selectedOptionValues.join(',');
    }
    if(href.indexOf('?option_values=') > -1){
      href = (href.substring(0, href.indexOf('?option_values=')+15) + optionValuesStr)
    }else{
      href += ("?option_values="+ optionValuesStr)
    }
    ele.dataset.href = href; 
  }
  onVariantChangeHover(event){
    // Hover-triggered clicks cause aborted fetches, wrong price HTML, and stuck .active borders on cards.
    if (this.dataset.layout === 'card') return;
    const label = event.currentTarget;
    const forId = label?.getAttribute('for');
    if (!forId) return;
    const input = document.getElementById(forId);
    if (!input || !this.contains(input)) return;
    input.click();
    this.updatelabelDataset(label);
  }
}
customElements.define('color-swatch', ColorSwatch);
// Scroll to top
window.onscroll = () => {
  scrollTopButton();
}
function scrollTopButton() {
  if (document.body.scrollTop > 400 || document.documentElement.scrollTop > 400) {
    document.getElementById('footer_scroll').classList.remove('scrollbtn_hide');
  } else {
    document.getElementById('footer_scroll').classList.add('scrollbtn_hide');
  }
}
// Collapsible content — panel: doğrudan kardeş .collapse veya (collections-tabs) önce gelen ul + sonra toggle
function getCollapsePanelForToggle(toggleEl, host) {
  var n = toggleEl.nextElementSibling;
  if (n && n.classList && n.classList.contains('collapse')) {
    return n;
  }
  var ul = host.querySelector('ul.collapse.tabs-collapse, ul.tabs-collapse.collapse');
  if (ul) return ul;
  n = toggleEl.nextElementSibling;
  while (n) {
    if (n.classList && n.classList.contains('collapse')) return n;
    n = n.nextElementSibling;
  }
  return null;
}
class CollapseContent extends HTMLElement {
  constructor() {
    super();
    var col = this.getElementsByClassName("toggle");
    Array.from(col).forEach((ele) => {
      ele.setAttribute('tabindex', '0');
      var content = getCollapsePanelForToggle(ele, this);
      var defaultOpen = ele.classList.contains("active");
      if (content && !defaultOpen) {
        content.style.height = '0px';
        content.setAttribute('data-collapsed', 'true');
      }
      ele.addEventListener("click", this.onSectionClick.bind(this));
      ele.addEventListener('keydown', this.handleKeyDown.bind(this));
    });
  }
  handleKeyDown(event){
    if (event.keyCode === 13) {
      this.onSectionClick(event);
    }
  }
  onSectionClick(event) {
    event.currentTarget.classList.toggle("active");
    var content = getCollapsePanelForToggle(event.currentTarget, this);
    if (!content) return;
    var isCollapsed = content.getAttribute('data-collapsed') === 'true';
    let mainElement = event.currentTarget.closest('.webicollapse_main')
    if(mainElement){
      mainElement.classList.toggle("active");
    }
    if (isCollapsed) {
      this.expandSection(content);
      content.setAttribute('data-collapsed', 'false');
    } else {
      this.collapseSection(content);
    }
  }
  expandSection(element) {
    var sectionHeight = element.scrollHeight;
    element.style.height = sectionHeight + 'px';
    element.style.visibility = 'visible';
    element.addEventListener('transitionend', () => {
      element.removeEventListener('transitionend', this.expandSection);
      element.style.height = null;
      element.style.visibility = 'visible';
    });
    element.setAttribute('data-collapsed', 'false');
  }
  collapseSection(element) {
    var sectionHeight = element.scrollHeight;
    element.style.height = sectionHeight + 'px';
    element.offsetHeight;
    element.style.transition = 'height 0.3s ease';
    element.style.height = '0px';
    element.addEventListener('transitionend', () => {
      element.removeEventListener('transitionend', this.collapseSection);
      element.style.transition = '';
      element.style.height = '0px';
      element.style.visibility = 'hidden';
    });
    element.setAttribute('data-collapsed', 'true');
  }
}
customElements.define('collapse-content', CollapseContent);
// Image comparison
class ImgCompare extends HTMLElement {
  constructor() {
    super();
    this.topImage = this.querySelectorAll('.compare_image')[1];
    this.inputCompare = this.querySelector('.compare_line');
    const compare_cursor = this.querySelector('#compare_cursor');
    compare_cursor.addEventListener('mousemove', this.resizeImg.bind(this));
    compare_cursor.addEventListener('touchmove', this.resizeImg.bind(this));
  }
  resizeImg(event){
    this.topImage.style.width = `${event.target.value}%`;
    this.inputCompare.style.left = `${event.target.value}%`;
  }
  connectedCallback() {
    this.querySelectorAll('.comparewidth').forEach(ele =>{
      ele.style.width = this.clientWidth + 'px';
    });
  }
}
customElements.define('img-compare', ImgCompare);
// Video
document.querySelectorAll('.video-content-remove').forEach((close) => {
  close.addEventListener('click', (event) => {
    const parentElement = event.currentTarget.closest('.video_box');
    if (parentElement) {
      parentElement.remove();
    }
  });
});
const switchFilter = document.getElementById('SwitchFilter');
const productGrid = document.getElementById('ProductGridContainer');
if (switchFilter && productGrid) {
  function updateProductGridClass() {
    if (switchFilter.checked) {
      switchFilter.setAttribute('data-product', 'model');
      productGrid.classList.add('model-image');
      productGrid.classList.remove('product-image');
    } else {
      switchFilter.setAttribute('data-product', 'product');
      productGrid.classList.remove('model-image');
      productGrid.classList.add('product-image');
    }
  }
  switchFilter.addEventListener('change', updateProductGridClass);
  updateProductGridClass();
}
class WbQuickView extends HTMLElement {
  constructor() { 
    super();
    this.addEventListener('click', this.wbPopUp.bind(this));
  }
  lockPageScroll() {
    if (document.body.classList.contains('modal_view') && document.body.style.position === 'fixed') return;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.dataset.modalScrollY = String(scrollY);
    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden', 'modal_view');
    document.body.style.top = `-${scrollY}px`;
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
  }
  unlockPageScroll() {
    const scrollY = parseInt(document.body.dataset.modalScrollY || '0', 10) || 0;
    document.documentElement.classList.remove('overflow-hidden', 'modal_view');
    document.body.classList.remove('overflow-hidden', 'modal_view');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    delete document.body.dataset.modalScrollY;
    window.scrollTo(0, scrollY);
  }
  wbPopUp(e) {
    const button = e.target.closest('button'); // Ensure we get the button element
    if (!button) return; // If not clicked on button or its child, do nothing

    // Cart drawer sits above quick view — close it so variant picker is visible
    const cartDrawer = button.closest('cart-drawer');
    if (cartDrawer && typeof cartDrawer.close === 'function') {
      cartDrawer.close();
    }

    button.classList.add('loading');
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) {
        spinner.classList.remove('hidden');
    }
    button.setAttribute('disabled', 'true');
    fetch(button.dataset.url?.split('?')[0] + '?view=' + button.dataset.template + 
          (button.dataset.variantid ? '&variant=' + button.dataset.variantid : ''))
        .then(response => response.text())
        .then(data => {
            if (!data) return;
            const parser = new DOMParser();
            const htmlDoc = parser.parseFromString(data, 'text/html');
            const html = htmlDoc.querySelector('[data-html="content"]').innerHTML;
            const modalSelector = document.querySelector(".js-quick_view");
            if (modalSelector) {
              this.lockPageScroll();
              modalSelector.innerHTML = html;
              modalSelector.querySelectorAll('.scroll-trigger, .c-scroll-trigger').forEach((el) => {
                el.classList.remove('scroll-trigger', 'c-scroll-trigger', 'animate--slide-in', 'animate--fade-in', 'scroll-trigger--offscreen');
                el.style.opacity = '1';
                el.style.visibility = 'visible';
                el.style.transform = 'none';
              });
              modalSelector.querySelectorAll('.toggle.active ~ .content_collase, .content_collase:not([data-collapsed="true"])').forEach((panel) => {
                panel.style.visibility = 'visible';
                panel.style.opacity = '1';
                if (panel.getAttribute('data-collapsed') !== 'true') {
                  panel.style.height = '';
                }
              });
              modalSelector.querySelectorAll('.product__tax a, .product__view-details, .social_share a, .accordion__content, .accordion__content *').forEach((el) => {
                el.style.color = '#000000';
                el.style.opacity = '1';
                el.style.visibility = 'visible';
              });
              modalSelector.querySelectorAll('.social_share svg, .social_share path, .product__view-details .icon, .product__view-details path').forEach((el) => {
                el.style.color = '#000000';
                el.style.fill = '#000000';
                el.style.opacity = '1';
                el.style.visibility = 'visible';
              });
              modalSelector.querySelectorAll('[data-accordion-figma]').forEach((root) => {
                if (typeof window.initProductAccordionFigma === 'function') {
                  window.initProductAccordionFigma(root);
                }
              });
              trapFocus(modalSelector.querySelector('.quick-add-modal__content-info'));
              modalSelector.querySelector('.quick-add-modal__content-info').focus();
              if (window.ProductModel) window.ProductModel.loadShopifyXR();
            }
            button.classList.remove('loading');
            if (spinner) {
                spinner.classList.add('hidden');
            }
            button.removeAttribute('disabled');
        })
    .catch(error => console.error('Error:', error));
  }
}
customElements.define('wb-quick-view', WbQuickView);
class WbModal extends HTMLElement {
  constructor() { 
    super();
    this.closeEl = this.querySelector(".modal__close button"); // Close button
    this.modalContent = this.querySelector(".quick-add-modal__content"); // Main content
    this.observeBodyClass();
  }
  connectedCallback() {
    const _ = this;
    if (this.closeEl) {
      this.closeEl.addEventListener("click", this.close.bind(this));
    }
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" || event.keyCode === 27) {
        _.close();
      }
    });
    // Close modal when clicking outside it
    this.addEventListener("click", function (event) {
      const isInsidePopup = _.modalContent.contains(event.target);
      const isCloseButton = event.target.closest(".modal__close button");
      if (!isInsidePopup || isCloseButton) {
        _.close();
      }
    });
  }
  observeBodyClass() {
    const observer = new MutationObserver((mutationsList) => {
      for (let mutation of mutationsList) {
        if (mutation.attributeName === "class") {
          if (document.body.classList.contains("overflow-hidden-drawer")) {
            this.close();
          }
        }
      }
    });
    observer.observe(document.body, { attributes: true });
  }
  unlockPageScroll() {
    const scrollY = parseInt(document.body.dataset.modalScrollY || '0', 10) || 0;
    document.documentElement.classList.remove('overflow-hidden', 'modal_view');
    document.body.classList.remove('overflow-hidden', 'modal_view');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    delete document.body.dataset.modalScrollY;
    window.scrollTo(0, scrollY);
  }
  close() {
    this.classList.add("close_popup");
    setTimeout(() => {
      this.remove();
      this.unlockPageScroll();
    }, 80);
  }
}
customElements.define("wb-modal", WbModal);
// Back to top
document.addEventListener('DOMContentLoaded', () => {
  const stickyProPage = document.querySelector('.sticky_pro_page');
  const footerScroll = document.querySelector('#footer_scroll');
  if (stickyProPage && footerScroll) {
    footerScroll.classList.add('product_btt');
  }
});
// sticky cart height
const stickyElement = document.querySelector('.sticky_pro_page');
if (stickyElement) {
  const stickyHeight = stickyElement.offsetHeight;
  document.documentElement.style.setProperty('--sticky-pro-page-height', `${stickyHeight}px`);
}

let tabFocus = false;
document.addEventListener('keydown', function(e) {
  if (e.key === 'Tab') {
    tabFocus = true;
  }
});
document.addEventListener('focusin', function(e) {
  if (tabFocus && e.target.matches('[name="quickView"]')) {
    e.target.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    });
  }
});
